import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errDetail } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Truck, Plus, Search, AlertTriangle } from "lucide-react";

const STATUS_COLORS = {
  prospect: "bg-gray-100 text-gray-700", registered: "bg-blue-100 text-blue-700",
  qualified: "bg-indigo-100 text-indigo-700", approved: "bg-green-100 text-green-700",
  restricted: "bg-amber-100 text-amber-800", blocked: "bg-red-100 text-red-700",
};
const TIER_COLORS = {
  strategic: "bg-[#1B2A4A] text-white", preferred: "bg-[#F5A623]/20 text-[#8a5a00]",
  approved: "bg-gray-100 text-gray-700", tail: "bg-gray-50 text-gray-500",
};
const STATUSES = ["prospect", "registered", "qualified", "approved", "restricted", "blocked"];
const TIERS = ["strategic", "preferred", "approved", "tail"];
const ROLE_LABELS = { supplier: "Lieferant", prospect_supplier: "Potenzieller Lieferant", customer: "Kunde" };

export default function Suppliers() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [tier, setTier] = useState("");
  const [caps, setCaps] = useState([]);
  const [dlg, setDlg] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (status) params.status = status;
    if (tier) params.tier = tier;
    api.get("/suppliers", { params }).then((r) => setRows(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, [q, status, tier]);

  useEffect(() => { const id = setTimeout(load, 250); return () => clearTimeout(id); }, [load]);
  useEffect(() => { api.get("/srm/my-caps").then((r) => setCaps(r.data.caps || [])).catch(() => {}); }, []);
  const canWrite = caps.includes("supplier_write");

  return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="suppliers-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-[#1B2A4A] flex items-center justify-center">
            <Truck className="h-6 w-6 text-[#F5A623]" strokeWidth={1.75} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#1B2A4A]">Lieferanten</h1>
            <p className="text-sm text-gray-500">Lieferantenstammdaten, Qualifizierung & Kreditoren</p>
          </div>
        </div>
        {canWrite && (
          <Button data-testid="supplier-new-btn" className="bg-[#1B2A4A] hover:bg-[#24365d]" onClick={() => setDlg(true)}>
            <Plus className="h-4 w-4 mr-1" /> Neuer Lieferant
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input data-testid="supplier-search" className="pl-9 w-64" placeholder="Suchen…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="w-44" data-testid="supplier-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Status</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={tier || "all"} onValueChange={(v) => setTier(v === "all" ? "" : v)}>
          <SelectTrigger className="w-44" data-testid="supplier-filter-tier"><SelectValue placeholder="Tier" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Tiers</SelectItem>
            {TIERS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nr.</TableHead><TableHead>Name</TableHead><TableHead>Ort</TableHead>
              <TableHead>Status</TableHead><TableHead>Tier</TableHead>
              <TableHead>Warengruppen</TableHead><TableHead>Rollen</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={7} className="text-center text-gray-400 py-8">Lädt…</TableCell></TableRow>}
            {!loading && rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-gray-400 py-8">Keine Lieferanten</TableCell></TableRow>}
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`supplier-row-${r.id}`} className="cursor-pointer hover:bg-gray-50" onClick={() => navigate(`/suppliers/${r.id}`)}>
                <TableCell className="font-mono text-xs">{r.account_number}</TableCell>
                <TableCell className="font-medium text-[#1B2A4A]">{r.name}</TableCell>
                <TableCell className="text-sm text-gray-600">{[r.city, r.country].filter(Boolean).join(", ")}</TableCell>
                <TableCell><Badge className={STATUS_COLORS[r.status] || "bg-gray-100"}>{r.status || "–"}</Badge></TableCell>
                <TableCell><Badge className={TIER_COLORS[r.tier] || "bg-gray-100"}>{r.tier || "–"}</Badge></TableCell>
                <TableCell className="text-xs text-gray-600">{(r.categories || []).join(", ")}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    {(r.roles || []).map((role) => (
                      <Badge key={role} variant="outline" className={role === "supplier" ? "border-[#1B2A4A] text-[#1B2A4A]" : role === "prospect_supplier" ? "border-indigo-400 text-indigo-700" : "border-[#F5A623] text-[#8a5a00]"}>{ROLE_LABELS[role] || role}</Badge>
                    ))}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <NewSupplierDialog open={dlg} onClose={(created) => { setDlg(false); if (created) { load(); navigate(`/suppliers/${created}`); } }} />
    </div>
  );
}

function NewSupplierDialog({ open, onClose }) {
  const navigate = useNavigate();
  const empty = { name: "", vat_id: "", duns_number: "", city: "", country: "", billing_address: "", role: "supplier" };
  const [form, setForm] = useState(empty);
  const [candidates, setCandidates] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setForm(empty); setCandidates(null); } }, [open]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (confirmDuplicate) => {
    setBusy(true);
    try {
      const r = await api.post("/suppliers", { ...form, confirm_duplicate: confirmDuplicate });
      toast.success(`${ROLE_LABELS[r.data.role] || "Lieferant"} ${r.data.account_number} angelegt`);
      onClose(r.data.account_id);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "exact_vat") {
        toast.error(`USt-IdNr. existiert bereits: ${d.existing?.name} (${d.existing?.account_number})`);
      } else if (d && typeof d === "object" && d.code === "possible_duplicates") {
        setCandidates(d.candidates || []);
      } else {
        toast.error(errDetail(e));
      }
    } finally { setBusy(false); }
  };

  // Convert an EXISTING company (from the duplicate list) into a supplier instead of creating a new one.
  const convertExisting = async (c) => {
    setBusy(true);
    try {
      const nextRoles = Array.from(new Set([...(c.roles || []), form.role]));
      await api.put(`/accounts/${c.id}/roles`, { roles: nextRoles, role: form.role });
      toast.success(`${c.name} als ${ROLE_LABELS[form.role]} übernommen`);
      onClose(null);
      navigate(`/suppliers/${c.id}`);
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={() => onClose(null)}>
      <DialogContent data-testid="supplier-new-dialog">
        <DialogHeader>
          <DialogTitle>Neuer Lieferant</DialogTitle>
          <DialogDescription>Stammdaten erfassen. Dubletten werden vor dem Speichern geprüft.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Firmenname *</Label><Input data-testid="supplier-field-name" value={form.name} onChange={set("name")} /></div>
          <div><Label>Rolle</Label>
            <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
              <SelectTrigger data-testid="supplier-field-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="supplier">Lieferant (aktiv)</SelectItem>
                <SelectItem value="prospect_supplier">Potenzieller Lieferant</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>USt-IdNr.</Label><Input data-testid="supplier-field-vat" value={form.vat_id} onChange={set("vat_id")} /></div>
            <div><Label>DUNS</Label><Input value={form.duns_number} onChange={set("duns_number")} /></div>
            <div><Label>Stadt</Label><Input value={form.city} onChange={set("city")} /></div>
            <div><Label>Land (Code)</Label><Input value={form.country} onChange={set("country")} placeholder="DE" /></div>
          </div>
        </div>
        {candidates && (
          <div className="mt-2 rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="supplier-duplicate-warning">
            <div className="flex items-center gap-2 text-amber-800 font-medium mb-2"><AlertTriangle className="h-4 w-4" /> Mögliche Dubletten</div>
            <ul className="text-sm space-y-2">
              {candidates.map((c) => (
                <li key={c.id} className="flex items-center justify-between gap-2">
                  <span>{c.name} ({c.account_number}) <span className="text-amber-700">{Math.round(c.match_score * 100)}% · {c.reasons.join(", ")}</span></span>
                  <Button size="sm" variant="outline" data-testid={`supplier-convert-${c.id}`} disabled={busy} onClick={() => convertExisting(c)}>Diese Firma übernehmen</Button>
                </li>
              ))}
            </ul>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(null)}>Abbrechen</Button>
          {candidates
            ? <Button data-testid="supplier-confirm-duplicate-btn" disabled={busy} className="bg-amber-600 hover:bg-amber-700" onClick={() => submit(true)}>Trotzdem neu anlegen</Button>
            : <Button data-testid="supplier-save-btn" disabled={busy || !form.name} className="bg-[#1B2A4A] hover:bg-[#24365d]" onClick={() => submit(false)}>Anlegen</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
